import type { EmbryoGenomicResult } from "@src/packages/cycles/hooks/useEmbryoReport";
import { getPolygenicRiskConditions } from "./embryoTestingOverview";

/** Presentation configuration, not a second model of report data. */
export type EmbryoComparisonColumn = {
  key: string;
  group: "embryology" | "single_gene" | "polygenic";
  labelKey?: string;
  condition?: string;
  defaultVisible: boolean;
};

export const EMBRYO_COMPARISON_IDENTITY_WIDTH = 92;

/** Preserve readable translated references without widening the columns. */
export function getEmbryoComparisonHeaderHeight(
  contentHeights: readonly number[],
) {
  return Math.ceil(Math.max(56, ...contentHeights.map((height) => height + 8)));
}

/** Keep ordinary rows dense; grow only for the visible row's actual content. */
export function getEmbryoComparisonRowHeight(
  identityHeight: number,
  cellHeights: readonly number[],
) {
  return Math.ceil(
    Math.max(
      44,
      identityHeight + 9,
      ...cellHeights.map((height) => height + 12),
    ),
  );
}

/** Content-sized minimums keep selected fields readable when scrolling. */
export function getEmbryoComparisonColumnMinWidth(
  column: Pick<EmbryoComparisonColumn, "key" | "group">,
) {
  if (column.group === "polygenic") return 55;
  if (column.group === "single_gene") return 84;
  if (column.key === "grade") return 40;
  if (column.key === "day") return 42;
  if (column.key === "finding") return 96;
  return 84;
}

/** Balance the available space equally, respecting each field's minimum. */
export function getEmbryoComparisonColumnWidths(
  columns: readonly Pick<EmbryoComparisonColumn, "key" | "group">[],
  availableWidth: number,
) {
  const minimums = columns.map(getEmbryoComparisonColumnMinWidth);
  const widths: number[] = [...minimums];
  let remainingWidth = Math.max(
    availableWidth,
    minimums.reduce((sum, width) => sum + width, 0),
  );
  let remainingColumns = columns.length;
  // Allocate the larger minimums first, then evenly share what is left.
  for (const { minimum, index } of minimums
    .map((minimum, index) => ({ minimum, index }))
    .sort((a, b) => b.minimum - a.minimum)) {
    const width = Math.max(minimum, remainingWidth / remainingColumns);
    widths[index] = width;
    remainingWidth -= width;
    remainingColumns -= 1;
  }
  return widths;
}

export function getEmbryoComparisonColumns(
  embryos: readonly Pick<
    EmbryoGenomicResult,
    "polygenicRisks" | "monogenicFindings"
  >[],
  { hasGrade, hasDay }: { hasGrade: boolean; hasDay: boolean },
): EmbryoComparisonColumn[] {
  const conditions = getPolygenicRiskConditions(embryos);
  const singleGenes = [
    ...new Set(
      embryos.flatMap((embryo) =>
        embryo.monogenicFindings.map((finding) => finding.condition),
      ),
    ),
  ].sort();
  const columns: EmbryoComparisonColumn[] = conditions.map(({ condition }) => ({
    key: `polygenic-${condition}`,
    condition,
    group: "polygenic",
    defaultVisible: true,
  }));
  for (const [key, labelKey, available, defaultVisible] of [
    ["grade", "care.embryo_report.col_grade", hasGrade, true],
    ["day", "care.embryo_report.compare.biopsy_day", hasDay, true],
    [
      "finding",
      "care.embryo_report.compare.chromosome_finding",
      embryos.length > 0,
      false,
    ],
    [
      "recommendation",
      "care.embryo_report.compare.labs_view_on_transfer",
      embryos.length > 0,
      false,
    ],
  ] as const) {
    if (available) {
      columns.push({ key, labelKey, group: "embryology", defaultVisible });
    }
  }
  return [
    ...columns,
    ...singleGenes.map((condition): EmbryoComparisonColumn => ({
      key: `single-gene-${condition}`,
      condition,
      group: "single_gene",
      defaultVisible: false,
    })),
  ];
}

export function visibleEmbryoComparisonColumns(
  columns: readonly EmbryoComparisonColumn[],
  visibility: Readonly<Record<string, boolean>>,
) {
  return columns.filter(
    (column) => visibility[column.key] ?? column.defaultVisible,
  );
}
